import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatService } from '../services/chatService';
import { ChatSession } from '../types/chat.types';
import { BalanceDisplay } from './BalanceDisplay';
import { RenameSessionDialog } from './RenameSessionDialog';
import { SettingsModal } from './SettingsModal';

interface User {
  id: string;
  email: string | null;
  name: string;
  isAnonymous: boolean;
  permissions: string[];
  mobile_number?: string | null;
}

interface NavigationDrawerProps {
  isAuthenticated: boolean;
  isAnonymous: boolean;
  isMobileOpen: boolean;
  onMobileClose: () => void;
  user: User | null;
  onNewChat: () => void;
  onLoadSession: (sessionId: string) => void;
  currentSessionId: string | null;
  onLogout: () => void;
  balance: number;
  isLowBalance: boolean;
  isZeroBalance: boolean;
  onRefreshSessions?: (refreshFn: () => Promise<void>) => void;
  onUserUpdate?: () => void;
}

export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  isAuthenticated,
  isAnonymous,
  isMobileOpen,
  onMobileClose,
  user,
  onNewChat,
  onLoadSession,
  currentSessionId,
  onLogout,
  balance,
  isLowBalance,
  isZeroBalance,
  onRefreshSessions,
  onUserUpdate,
}) => {
  const navigate = useNavigate();
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    const saved = localStorage.getItem('navDrawerPinned');
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [sessionMenuOpenId, setSessionMenuOpenId] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [sessionToRename, setSessionToRename] = useState<ChatSession | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const sessionMenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Close session menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sessionMenuOpenId) {
        const menuRef = sessionMenuRefs.current[sessionMenuOpenId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setSessionMenuOpenId(null);
        }
      }
    };

    if (sessionMenuOpenId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sessionMenuOpenId]);

  // Detect mobile screen size (< 768px = md breakpoint)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load sessions function
  const loadSessions = async () => {
    if (!isAuthenticated || isAnonymous) {
      setSessions([]);
      return;
    }

    setIsLoadingSessions(true);
    try {
      const loadedSessions = await chatService.getSessions(10);
      setSessions(loadedSessions);
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Load sessions when authenticated
  useEffect(() => {
    loadSessions();
  }, [isAuthenticated, isAnonymous]);

  // Expose loadSessions to parent via callback
  useEffect(() => {
    if (onRefreshSessions) {
      onRefreshSessions(loadSessions);
    }
  }, [onRefreshSessions]);

  // Don't show drawer for anonymous users
  if (!isAuthenticated || isAnonymous) {
    return null;
  }

  // On mobile: only show if explicitly opened, can't be pinned
  // On desktop: show if pinned (no hover expand)
  const isExpanded = isMobile ? true : isPinned;
  const isVisible = isMobile ? isMobileOpen : true;

  const togglePin = () => {
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    localStorage.setItem('navDrawerPinned', String(newPinned));
  };

  const handleNavItemClick = (action: () => void) => {
    action();
    // Close mobile drawer when nav item is clicked
    if (isMobile) {
      onMobileClose();
    }
  };

  const handleSessionClick = (sessionId: string) => {
    onLoadSession(sessionId);
    if (isMobile) {
      onMobileClose();
    }
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    onLogout();
  };

  const handleSettings = () => {
    setIsUserMenuOpen(false);
    setIsSettingsOpen(true);
  };

  const handleAdminConsole = () => {
    setIsUserMenuOpen(false);
    navigate('/admin/console');
  };

  const handleManagePlan = () => {
    setIsUserMenuOpen(false);
    navigate('/manage-plan');
  };

  const handleRenameClick = (session: ChatSession) => {
    setSessionToRename(session);
    setRenameDialogOpen(true);
    setSessionMenuOpenId(null);
  };

  const handleRenameSubmit = async (newTitle: string) => {
    if (!sessionToRename) return;

    try {
      await chatService.renameSession(sessionToRename.id, newTitle);

      // Update the session in the local state
      setSessions((prevSessions) =>
        prevSessions.map((s) =>
          s.id === sessionToRename.id ? { ...s, title: newTitle } : s
        )
      );

      setRenameDialogOpen(false);
      setSessionToRename(null);
    } catch (error) {
      console.error('Error renaming session:', error);
      // You could show an error toast here
    }
  };

  const handleDeleteClick = async (sessionId: string) => {
    setSessionMenuOpenId(null);

    // Show confirmation
    if (!window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      return;
    }

    try {
      await chatService.deleteSession(sessionId);

      // Remove the session from the local state
      setSessions((prevSessions) => prevSessions.filter((s) => s.id !== sessionId));

      // If the deleted session was the current session, start a new one
      if (currentSessionId === sessionId) {
        await onNewChat();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      // You could show an error toast here
    }
  };

  // Check if user has admin.access permission
  const hasAdminAccess = user?.permissions?.includes('admin.access') || false;

  const navItems = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      label: 'New Chat',
      onClick: onNewChat,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      label: 'Resources',
      onClick: () => console.log('Navigate to resources'),
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      label: 'Settings',
      onClick: () => console.log('Navigate to settings'),
    },
  ];

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && isVisible && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={onMobileClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed left-0 top-0 bg-[#F5F5F5] text-[#080808] shadow-lg transition-all duration-300 ease-in-out flex flex-col ${
          isMobile
            ? `z-50 w-64 h-full max-h-screen transform ${isVisible ? 'translate-x-0' : '-translate-x-full'}`
            : `z-40 h-screen ${isExpanded ? 'w-64' : 'w-16'}`
        }`}
      >
        {/* Header with pin button (desktop only) or close button (mobile) */}
        <div className="flex items-center justify-between p-4 border-b border-gray-300 flex-shrink-0">
          {isMobile ? (
            <>
              <img
                src="/images/logos/icon-color.svg"
                alt="ChatHTL"
                className="h-8 w-8"
              />
              <button
                onClick={onMobileClose}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : isExpanded ? (
            <>
              <img
                src="/images/logos/icon-color.svg"
                alt="ChatHTL"
                className="h-8 w-8"
              />
              <button
                onClick={togglePin}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title={isPinned ? 'Unpin drawer' : 'Pin drawer'}
              >
                {isPinned ? (
                  <svg className="w-5 h-5 text-[#74AA9C]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={togglePin}
              className="p-1 hover:bg-gray-200 rounded transition-colors mx-auto"
              title="Expand drawer"
            >
              <img
                src="/images/logos/icon-color.svg"
                alt="ChatHTL"
                className="h-8 w-8"
              />
            </button>
          )}
        </div>

      {/* Navigation items */}
      <nav className="flex-1 overflow-y-auto pt-4 pb-8">
        {navItems.map((item, index) => (
          <button
            key={index}
            onClick={() => handleNavItemClick(item.onClick)}
            className={`flex items-center w-full p-4 hover:bg-gray-200 transition-colors text-[#080808] ${
              isExpanded ? 'justify-start' : 'justify-center'
            }`}
            title={!isMobile && !isExpanded ? item.label : undefined}
          >
            <div className="flex-shrink-0 text-[#74AA9C]">{item.icon}</div>
            {isExpanded && (
              <span className="ml-3 text-sm font-medium whitespace-nowrap">
                {item.label}
              </span>
            )}
          </button>
        ))}

        {/* Chats Section */}
        {isExpanded && (
          <div className="mt-6">
            <div className="px-4 mb-2">
              <h3 className="text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider">
                Chats
              </h3>
            </div>
            {isLoadingSessions ? (
              <div className="px-4 py-2 text-xs text-[#4A4A4A]">Loading...</div>
            ) : sessions.length === 0 ? (
              <div className="px-4 py-2 text-xs text-[#4A4A4A]">No chat history</div>
            ) : (
              <div className="space-y-1">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`relative flex items-start w-full px-4 py-2 hover:bg-gray-200 transition-colors group ${
                      currentSessionId === session.id ? 'bg-gray-200' : ''
                    }`}
                  >
                    <button
                      onClick={() => handleSessionClick(session.id)}
                      className="flex items-start flex-1 text-left min-w-0"
                      title={session.title}
                    >
                      <div className="flex-shrink-0 mt-1 text-[#74AA9C]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div className="ml-3 overflow-hidden flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-[#080808]">{session.title}</p>
                        <p className="text-xs text-[#4A4A4A]">
                          {session.lastMessageAt.toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                          })}
                          {' • '}
                          {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>

                    {/* Menu button - shown on hover or when menu is open */}
                    <div
                      ref={(el) => {
                        sessionMenuRefs.current[session.id] = el;
                      }}
                      className="relative flex-shrink-0"
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSessionMenuOpenId(sessionMenuOpenId === session.id ? null : session.id);
                        }}
                        className={`p-1 rounded hover:bg-gray-300 transition-colors ${
                          sessionMenuOpenId === session.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                        title="Session options"
                      >
                        <svg className="w-4 h-4 text-[#4A4A4A]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>

                      {/* Session menu dropdown */}
                      {sessionMenuOpenId === session.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[140px]">
                          <button
                            onClick={() => handleRenameClick(session)}
                            className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Rename
                          </button>
                          <button
                            onClick={() => handleDeleteClick(session.id)}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Footer with balance and user info */}
      {user && (
        <div ref={userMenuRef} className="relative flex-shrink-0 border-t border-gray-300">
          {/* Balance Display (only when expanded) */}
          {isExpanded && (
            <BalanceDisplay
              location="drawer"
              balance={balance}
              isLowBalance={isLowBalance}
              isZeroBalance={isZeroBalance}
            />
          )}

          {/* User Menu Dropdown (only when expanded) */}
          {isExpanded && isUserMenuOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
              {/* Email at top */}
              <div className="px-4 py-2 border-b border-gray-200">
                <p className="text-xs text-[#6B7280] truncate">
                  {user.email || 'Authenticated'}
                </p>
              </div>

              {/* Menu items */}
              <button
                onClick={handleSettings}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>

              <button
                onClick={handleManagePlan}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Manage Plan
              </button>

              <button
                onClick={() => navigate('/policies')}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Terms & Policies
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Log out
              </button>

              {/* Admin Console at bottom with separator */}
              {hasAdminAccess && (
                <>
                  <div className="border-t border-gray-200 my-2"></div>
                  <button
                    onClick={handleAdminConsole}
                    className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Admin Console
                  </button>
                </>
              )}
            </div>
          )}

          {/* Clickable user info */}
          <button
            onClick={() => isExpanded ? setIsUserMenuOpen(!isUserMenuOpen) : togglePin()}
            className={`w-full p-4 hover:bg-gray-200 transition-colors ${isExpanded ? 'text-left' : 'flex justify-center'}`}
          >
            {isExpanded ? (
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#74AA9C] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-white">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div className="ml-3 overflow-hidden flex-1">
                  <p className="text-sm font-medium truncate text-[#080808]">{user.name || 'User'}</p>
                  <p className="text-xs text-[#4A4A4A] truncate">
                    {user.email || 'Authenticated'}
                  </p>
                </div>
                <svg
                  className={`w-4 h-4 text-[#4A4A4A] transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            ) : (
              <div className="w-8 h-8 bg-[#74AA9C] rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
            )}
          </button>
        </div>
      )}
      </div>

      {/* Rename Session Dialog */}
      <RenameSessionDialog
        isOpen={renameDialogOpen}
        currentTitle={sessionToRename?.title || ''}
        onClose={() => {
          setRenameDialogOpen(false);
          setSessionToRename(null);
        }}
        onRename={handleRenameSubmit}
      />

      {/* Settings Modal */}
      {user && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          user={user}
          onUserUpdate={onUserUpdate}
        />
      )}
    </>
  );
};
